"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ZoomIn, X } from "lucide-react";
import { useSelectedVariantImage } from "@/contexts/SelectedVariantContext";

interface ProductGalleryProps {
  images: string[];
  name: string;
}

export default function ProductGallery({ images, name }: ProductGalleryProps) {
  const { images: variantImages } = useSelectedVariantImage();
  const [manualIndex, setManualIndex] = useState<number | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);

  // Seçili varyantın kendi görselleri varsa galeri onlara geçer, yoksa ürünün genel görsellerine döner
  const effectiveImages = variantImages.length > 0 ? variantImages : images;

  useEffect(() => {
    setManualIndex(null);
  }, [variantImages]);

  useEffect(() => {
    setIsTouch(window.matchMedia("(pointer: coarse)").matches);
    setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  const activeIndex = manualIndex ?? 0;
  const heroSrc = effectiveImages[activeIndex];

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isTouch || reducedMotion) return;
      const rect = frameRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    },
    [isTouch, reducedMotion]
  );

  if (!heroSrc) {
    return (
      <div className="w-full max-w-[420px] mx-auto aspect-[9/16] bg-[#111114] border border-[rgba(201,168,76,0.12)] rounded-[18px] flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#C9A84C]/5 to-transparent pointer-events-none" />
        <div className="w-32 h-56 relative">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-12 h-8 bg-[#C9A84C]/40 border border-[#C9A84C]/40 rounded-t-sm" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#C9A84C]/20 via-[#1A1A1F] to-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-md shadow-2xl" />
          <div className="absolute inset-0 flex flex-col items-center justify-center opacity-40">
            <span className="font-serif text-2xl text-[#C9A84C] italic">M·P</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      {/* Küçük Görseller — ana görselin solunda, aynı 9:16 oranında dikey dizi */}
      {effectiveImages.length > 1 && (
        <div className="flex flex-col gap-3 shrink-0">
          {effectiveImages.slice(0, 6).map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setManualIndex(i)}
              aria-label={`${name} — görsel ${i + 1}`}
              aria-current={activeIndex === i}
              className={`w-16 aspect-[9/16] relative rounded-lg overflow-hidden border-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B] ${
                activeIndex === i
                  ? "border-[#C9A84C] opacity-100"
                  : "border-[rgba(201,168,76,0.15)] opacity-50 hover:opacity-80"
              }`}
            >
              <Image
                src={img}
                alt={`${name} — küçük görsel ${i + 1}`}
                fill
                quality={90}
                loading="lazy"
                className="object-cover"
                sizes="64px"
              />
            </button>
          ))}
        </div>
      )}

      {/* Ana Görsel */}
      <div className="flex-1 min-w-0 max-w-[420px]">
        <div
          ref={frameRef}
          onMouseEnter={() => !isTouch && !reducedMotion && setIsZooming(true)}
          onMouseLeave={() => setIsZooming(false)}
          onMouseMove={handleMouseMove}
          onClick={() => setLightboxOpen(true)}
          className="relative aspect-[9/16] rounded-[18px] overflow-hidden border border-[#C9A84C]/25 shadow-[0_20px_60px_rgba(0,0,0,0.55),0_0_40px_rgba(201,168,76,0.08)] cursor-zoom-in bg-[#111114]"
        >
          <Image
            key={heroSrc}
            src={heroSrc}
            alt={`${name} — ana ürün görseli`}
            fill
            quality={90}
            priority
            sizes="(max-width: 768px) 100vw, 420px"
            className="object-cover transition-transform duration-300 ease-out"
            style={
              isZooming
                ? { transform: "scale(2.1)", transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%` }
                : { transform: "scale(1)", transformOrigin: "center" }
            }
          />

          {/* İmleci takip eden büyüteç halkası */}
          {isZooming && (
            <div
              className="pointer-events-none absolute w-16 h-16 rounded-full border-2 border-[#C9A84C] bg-[#C9A84C]/10 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${zoomOrigin.x}%`, top: `${zoomOrigin.y}%` }}
            />
          )}

          {/* Yakınlaştır rozeti */}
          {!isTouch && (
            <div className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm border border-[#C9A84C]/20 rounded-full px-3 py-1.5 text-[10px] text-[#C9A84C] uppercase tracking-widest">
              <ZoomIn size={12} /> Yakınlaştır
            </div>
          )}
        </div>

        {/* Nokta Göstergesi */}
        {effectiveImages.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            {effectiveImages.slice(0, 6).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setManualIndex(i)}
                aria-label={`Görsel ${i + 1}'e git`}
                aria-current={activeIndex === i}
                className={`h-1.5 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C] ${
                  activeIndex === i
                    ? "w-6 bg-[#C9A84C]"
                    : "w-1.5 bg-[rgba(201,168,76,0.25)] hover:bg-[rgba(201,168,76,0.4)]"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tam Ekran Lightbox — masaüstünde tıklama, mobilde dokunma ile açılır */}
      {lightboxOpen && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4 animate-in fade-in duration-200"
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            aria-label="Kapat"
            className="absolute top-6 right-6 text-[#F4F0E8] hover:text-[#C9A84C] transition"
          >
            <X size={28} />
          </button>
          <div
            className="relative w-full max-w-lg aspect-[9/16]"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={heroSrc}
              alt={`${name} — büyük görsel`}
              fill
              quality={95}
              className="object-contain"
              sizes="100vw"
            />
          </div>
        </div>
      )}
    </div>
  );
}
